import { EventEmitter, Injectable } from '@angular/core';

import { StorageService } from '../storage/storage.service';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';

export type UserTypes = 'PF' | 'PJ' | 'ADM' | '';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  token: string | undefined = undefined;
  _user: NewLoginResponse | undefined = undefined;

  activeUser: NewLoginResponse | undefined = undefined;

  userAuth: 'authStep' | 'recoveryStep' = 'authStep';
  code: string = '0000';

  visible_accounts: any[] = [];

  userTypePermission: string = '';
  forgotEmail: string = '';
  cpfAuthenticate: boolean = false;
  hidedForgotEmail: string = '';
  forgotValue: string = '';
  permissions: PermissionValue[] = [];
  navigationPermissions: MenuItem[] = [];
  accountsArr: RegisterUserResponse[] = [];

  finances: FinancePFResponse | undefined;
  userChange = new EventEmitter<NewLoginResponse | undefined>();
  financesFetched = new EventEmitter<FinancePFResponse>();
  registerRefetchEvent = new EventEmitter();
  visibleAccountsFetched = new EventEmitter();

  //   TODO - mudar para outro service
  reloadApprovalEvent = new EventEmitter();
  menuLoadedEvent = new EventEmitter();
  reloadComplementaryDataEvent = new EventEmitter();
  permissionsLoadedEvent = new EventEmitter();

  get user() {
    return this._user;
  }

  set user(user: NewLoginResponse | undefined) {
    this._user = user;
    this.userChange.emit(user);
  }

  constructor(
    private storageService: StorageService,
    private toast: ToastService,
    private router: Router
  ) {
    const user = this.storageService.getUser();
    if (user) this.user = user;

    const token = this.storageService.getToken();
    if (token) this.token = token;
  }

  setToken(token: string) {
    this.storageService.setToken(token);
    this.token = token;
  }

  getToken(): string | undefined {
    if (this.token) {
      return this.token;
    }

    const storedToken = this.storageService.getToken();

    if (storedToken) {
      this.token = storedToken;
      return this.token;
    }

    return undefined;
  }

  setUser(user: NewLoginResponse) {
    this.storageService.setUser(user);
    this.user = user;
  }

  updateUser(user: Partial<NewLoginResponse>) {
    const newUser: NewLoginResponse = Object.assign(this.user, user);
    this.setUser(newUser);
  }

  getUser(): NewLoginResponse | undefined {
    if (this.user) {
      return this.user;
    }

    const storedUser = this.storageService.getUser();

    if (storedUser) {
      this.user = storedUser;
      return storedUser;
    }

    return undefined;
  }

  setPermissionType(type: string) {
    this.storageService.setPermissionType(type);
    this.userTypePermission = type;
  }

  getPermissionType() {
    if (this.userTypePermission) {
      return this.userTypePermission;
    }

    const storedType = this.storageService.getPermissionType();

    if (storedType) {
      this.userTypePermission = storedType;
      return storedType;
    }

    return '';
  }

  setActiveUser(user: NewLoginResponse) {
    this.activeUser = user;
  }

  getActiveUser(): NewLoginResponse | undefined {
    return this.activeUser;
  }

  setUserAuth(step: 'authStep' | 'recoveryStep', code: string) {
    this.userAuth = step;
    this.code = code;
  }

  getUserAuth() {
    return this.userAuth;
  }

  async clearUser(): Promise<void> {
    // Limpar storage primeiro para evitar que getUser() recupere dados
    await this.storageService.logout();

    // Depois limpar estado em memória
    this.user = undefined;
    this.token = undefined;
    this.activeUser = undefined;
    this.userTypePermission = '';
    this.permissions = [];
    this.navigationPermissions = [];
    this.accountsArr = [];

    // Emitir evento para notificar que a limpeza foi concluída
    this.userChange.emit(undefined);
  }

  canViewPage(condition: string[], redirect: boolean = true) {
    // Se for master, sempre permite acesso
    if (this.isMasterUser()) {
      return true;
    }

    // Se for colaborador comum, permite acesso aos 5 menus específicos
    if (this.isCommonCollaborator()) {
      const commonCollaboratorPermissions = [
        'de_can_view_my_area', // Meu departamento
        'de_can_view_my_registers', // Meus cadastros
        'de_can_view_invites', // Convites
        'de_can_view_registers', // Cadastros
        'de_can_view_fund', // Fundos
      ];

      // Verifica se a permissão solicitada é uma das permitidas para colaborador comum
      if (
        condition.some((perm) => commonCollaboratorPermissions.includes(perm))
      ) {
        return true;
      }
    }

    if (!this.verifyPermission(condition)) {
      if (redirect) {
        this.toast.show(
          'error',
          'Aviso',
          'Você não tem permissão para acessar essa página.'
        );

        this.router.navigateByUrl('/app/dashboard');
      } else {
        return false;
      }
    }
    return true;
  }

  mapPermissionsSlugs = (viewGroup: string, obj: any) => {
    const mappedPermissionsSlugs = Object.keys(obj).map((key) => {
      const cleanKey = key.replace(/^can/, '');
      const parts = (cleanKey.match(/[A-Z][a-z]+/g) as string[]) || [];

      const action = parts[0]?.toLowerCase() || '';
      const hasDetails = parts.includes('Details');
      const extra = parts
        .filter((part) => part !== 'Details' && part !== parts[0])
        .map((p) => p.toLowerCase())
        .join('_');

      if (hasDetails) {
        return extra
          ? `de_can_${action}_details_${viewGroup}_${extra}`
          : `de_can_${action}_details_${viewGroup}`;
      }

      return extra
        ? `de_can_${action}_${viewGroup}_${extra}`
        : `de_can_${action}_${viewGroup}`;
    });

    return mappedPermissionsSlugs;
  };

  /**
   * Verifica se o usuário é Master baseado nas regras:
   * - type_permission === "master" OU type_permission === "owner" OU is_approver === true
   */
  isMasterUser(): boolean {
    const user = this.getUser();
    if (!user) {
      return false;
    }
    // Verificação: type_permission === "master" OU type_permission === "owner" OU is_approver === true
    return (
      user.type_permission === 'master' ||
      user.type_permission === 'owner' ||
      user.is_approver === true
    );
  }

  /**
   * Determina se o usuário é Colaborador Comum baseado nas regras:
   * - type_permission === "common" E (department NÃO é null OU role.applicable === "admin")
   */
  isCommonCollaborator(): boolean {
    const user = this.getUser();
    if (!user || !user.active_register) {
      return false;
    }
    const activeRegister = user.active_register.register;

    // Verifica se é type_permission === 'common'
    if (user.type_permission !== 'common') {
      return false;
    }

    // É Colaborador Comum se:
    // 1. Tem department não nulo, OU
    // 2. role.applicable === 'admin' (mesmo sem department)
    // MAS NÃO pode ser 'representative'
    return (
      (activeRegister.department !== null ||
        activeRegister.role?.applicable === 'admin') &&
      activeRegister.role?.applicable !== 'representative'
    );
  }

  verifyPermission = (codiname: string | string[] = []) => {
    // Se for master, sempre retorna true (tem todas as permissões)
    if (this.isMasterUser()) {
      return true;
    }

    const search = typeof codiname === 'string' ? [codiname] : codiname;

    const mappedPerms = this.permissions
      ? this.permissions.map((permission) => permission.codename)
      : [];

    const hasPerm = search.every((perm) => mappedPerms.includes(perm));

    return hasPerm;
  };
}
